import { doc, type Unsubscribe } from '@firebase/firestore';
import { db } from './firebase';
import { onSnapshotWithRetry } from './snapshotRetry';
import { mapDoc } from '@utils/firestore';
import { AreaClaimSummariesSchema } from '../schemas/firestoreSchemas';
import type { MapClaimSummary } from '../types';

/**
 * Live claim state for the map's pin layer: one function-maintained
 * summary doc per service area (claimSummaries/{areaId}) instead of a
 * subscription over every claim doc in the area. Each snapshot bills one
 * read per changed doc rather than one per claim.
 */
export function subscribeToAreaClaimSummaries(
  areaId: string,
  onChange: (structures: Record<string, MapClaimSummary>) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  return onSnapshotWithRetry(
    doc(db, 'claimSummaries', areaId),
    (snap) => {
      if (!snap.exists()) {
        onChange({});
        return;
      }
      const parsed = mapDoc(snap, AreaClaimSummariesSchema);
      if (!parsed) {
        onChange({});
        return;
      }
      const out: Record<string, MapClaimSummary> = {};
      for (const structureId in parsed.structures) {
        const entry = parsed.structures[structureId];
        out[structureId] = {
          claimedBy: entry.claimedBy,
          progressSnapshot: { completed: entry.completed ?? 0, total: entry.total ?? 0 },
        };
      }
      onChange(out);
    },
    (err) => onError?.(err)
  );
}
